import request from '@/modules/base/util/request'
import { isNil, merge, pickBy } from 'lodash'
import revenueConfig from './config'
import { BankTransferFormModel, BankTransferRequestModel } from '@/modules/banking/models'
import globalConfig from '@/config'
import { getList, LIST_TYPES, QUERY_TYPES } from '@/redux/ducks/lists'

const url = `${revenueConfig.searchUrl}`
const defaultOptions = {
  method: 'GET',
  auth: true,
  isConcurrent: true,
}

class RevenueRequestFactory {
  constructor(searchTerm) {
    // encodeURIComponent
    this._searchTerm = searchTerm || ''
    this._state = 'all'
    this._limit = 0
    this._offset = 0
  }
  attributes(attributes) {
    this._attributes = attributes
    return this
  }
  state(state) {
    this._state = state
    return this
  }
  limit(value) {
    this._limit = value
    return this
  }
  offset(value) {
    this._offset = value
    return this
  }
  send() {
    const data = pickBy({
      term: this._searchTerm,
    }, value => value !== isNil)
    const options = Object.assign({}, defaultOptions, { data })
    return request(url, options)
  }
}

export default class RevenueService {
  static find(searchTerm) {
    return new RevenueRequestFactory(searchTerm)
  }

  static createBankTransferRequestData(booking) {
    const {
      accountHolder,
      bookingAmount: amount,
      bookingDate: executionDate,
      purposeDescription,
      bankAccountId,
      iban,
      bic,
    } = booking

    return new BankTransferRequestModel({
      accountHolder,
      iban,
      bic,
      executionDate,
      purposeDescription,
      amount,
      bankAccountId,
    })
  }

  static createBankTransferFormData(revenue) {
    const {
      receiptNumber: usage,
      outstandingAmount: amount,
      payee,
      supplier,
    } = revenue

    let accountHolder = revenue.payee || ''
    let bic = revenue.payeeBic || ''
    let iban = revenue.payeeIban || ''

    if (supplier && Array.isArray(supplier.bankAccounts) && supplier.bankAccounts.length > 0) {
      accountHolder = supplier.bankAccounts[0].accountHolder || accountHolder
      bic = supplier.bankAccounts[0].bic || bic
      iban = supplier.bankAccounts[0].iban || iban
    }

    return new BankTransferFormModel({
      amount,
      accountHolder,
      bic,
      iban,
      payee,
      usage,
    })
  }

  static getList(options) {
    return new Promise((resolve, reject) => {
      request(revenueConfig.urls.resource, {
        auth: true,
        data: options,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static getById(id) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/${id}`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static getByFinanceApiId(financeApiId, onlyId = false) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/booking/${financeApiId}?onlyId=${onlyId}`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static copy(id) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/${id}/copy`, {
        auth: true,
      })
        .then(result => resolve(result.body))
        .catch(reject)
    })
  }

  static getListSetting() {
    return new Promise((resolve, reject) => {
      request(revenueConfig.urls.listSetting, { auth: true, isConcurrent: true })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static updateListSort(data) {
    return new Promise((resolve, reject) => {
      request(revenueConfig.urls.sort, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data,
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static updateListColumns(columns) {
    return new Promise((resolve, reject) => {
      request(revenueConfig.urls.columns, {
        auth: true,
        isConcurrent: true,
        method: 'PUT',
        data: columns,
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static deleteById(id) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/${id}`, {
        auth: true,
        method: 'DELETE',
      })
        .then(resolve)
        .catch(reject)
    })
  }

  static create(data) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}`, {
        auth: true,
        isConcurrent: true,
        method: 'POST',
        data,
      })
        .then((result) => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static update(id, data) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/${id}`, {
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        method: 'PUT',
        data,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static updateTags(id, data) {
    return new Promise((resolve, reject) => {
      request(`${revenueConfig.urls.resource}/${id}/tags`, {
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        method: 'PUT',
        data,
      })
        .then(result => {
          const { body } = result
          resolve(body)
        })
        .catch(reject)
    })
  }

  static async getPreviousAndNextRevenue(id, customFilters) {
    return this.getPreviousAndNextListRow(`revenue:${id}`, id, { customFilters })
  }

  // FLAG:useDocuAi — twin of ExpenseService.getPreviousAndNextListRow, same reasons: the revenue
  // list unions bookings with still-to-review documents, so the walk is anchored on a rowKey
  // ('revenue:<id>' / 'document:<fileId>') and the review component can walk it too.
  static async getPreviousAndNextListRow(rowKey, fallbackId, { customFilters = null, requireAnchor = false } = {}) {
    try {
      // get the current sort and filters for the list so the previous and next are equal to the list items
      const revenueListSettingsResponse = await this.getListSetting()
      const { meta: { defaultSort, sortBy } } = revenueListSettingsResponse
      const sortModel = sortBy.length
        ? sortBy.map((sort) => ({ sort: sort.direction, colId: sort.name }))
        : defaultSort.map((sort) => ({ sort: sort.direction, colId: sort.name }))
      const { searchTerm, quickFilter, filterItems } = getList(LIST_TYPES.REVENUE_LIST_TYPE)
      let filters = {
        filterModel: quickFilter,
        filterItems,
        searchTerm,
        sortModel,
        queryType: QUERY_TYPES.DATA,
      }
      if (customFilters) {
        filters = merge(filters, customFilters)
      }
      const response = await request(revenueConfig.routes.list, {
        method: 'POST',
        auth: true,
        isConcurrent: true,
        timeout: globalConfig.requestExtraLongTimeout,
        data: {
          ...filters,
          filterModel: filters.filterModel || {},
        },
      })
      const { body: { data: revenues } } = response

      // FLAG:useDocuAi — file ids and revenue ids collide, which is why the list carries `rowKey`;
      // matching on the bare id could land on the wrong row type. Flag-OFF rows have no rowKey and
      // no review rows at all, so the fallback keeps that path unchanged.
      const currentIndex = revenues.findIndex((row) => (
        row.rowKey ? row.rowKey === rowKey : row.id === fallbackId
      ))
      if (requireAnchor && currentIndex < 0) {
        return { previous: undefined, next: undefined }
      }
      // Full rows, not ids: the caller has to know a review row is NOT a revenue, or it navigates
      // to /revenue/edit/<fileId> and the API answers NOT_FOUND.
      return { previous: revenues[currentIndex - 1], next: revenues[currentIndex + 1] }
    } catch (error) {
      throw error
    }
  }

  static async reAnalyze(id) {
    return request(`${revenueConfig.urls.resource}/${id}/reanalyze`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async reAnalyzeAll() {
    return request(`${revenueConfig.urls.resource}/reanalyze`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async unlinkBookings(id) {
    return request(`${revenueConfig.urls.resource}/${id}/bookings/unlink`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'POST',
    })
  }

  static async deleteRevenueReceipt(id) {
    return request(`${revenueConfig.urls.resource}/receipt/${id}`, {
      auth: true,
      isConcurrent: true,
      timeout: globalConfig.requestExtraLongTimeout,
      method: 'DELETE',
    })
  }

  static async markAsSeen(id) {
    return request(`${revenueConfig.urls.resource}/${id}/markAsSeen`, {
      auth: true,
      isConcurrent: true,
      method: 'PUT',
    })
  }
}
